import React, { useMemo, useState } from "react";
import styled from "@emotion/styled";
import { ThemeProvider } from "emotion-theming";
import { breakpoints, theme } from "../../utils/styles";
import { Global } from "@emotion/core";
import { graphql, Link } from "gatsby";
import BlogList from "../../components/BlogList";
import SiteFooter from "../../components/layout/Footer";
import HeroVideo from "../../assets/273713_medium.mp4";
import MEDIA_LINKS from "../../utils/mediaLinks";

const Page = styled.main({
  minHeight: '100vh',
  background: theme.black,
  color: theme.white,
  margin: 0,
  padding: 0,
  position: 'relative',
});

// The shared site Footer is normally `position: fixed` with a negative
// z-index (see components/layout/Footer/styles.js), revealed only via a
// scroll-distance spacer tuned to the homepage's section stack. This page's
// layout is too different for that to reliably work, so pin it back into
// normal flow here — same design, rendered as an ordinary block instead.
const StaticFooterWrap = styled.div`
  footer {
    position: static;
    bottom: auto;
    z-index: auto;
  }

  /* The spacer div rendered before the footer is sized for the homepage's
     scroll-reveal trick (hundreds of px); here the footer is a normal block,
     so it only needs a modest visual gap. */
  > div:first-of-type {
    margin-bottom: 0 !important;
  }
`;

const FullWidthContainer = styled.div`
  width: 100vw;
  height: 220px;
  margin: 0;
  padding: 0;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
  overflow: hidden;
  background: radial-gradient(ellipse at top left, ${theme.primary}33, transparent 60%), ${theme.black};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  width: 100vw;
  margin: 0;
  padding: 0;
  @media (max-width: ${breakpoints.l}px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.section`
  background: ${theme.black};
  color: ${theme.white};
  padding: 40px;
  display: flex;
  flex-direction: column;
  min-height: 75vh;
`;

const NewsPanel = styled.div`
  flex: 1;
  max-height: calc(6 * 85px); /* Slightly more height, tighter items */
  overflow-y: auto;
  scroll-behavior: smooth;
  direction: rtl; /* Right-to-left to move scrollbar to left */
  margin-right: 40px; /* Reduced margin for better proportion */
  margin-top: 16px; /* Add some breathing room from title */
  
  /* Reset text direction for content */
  & > * {
    direction: ltr;
  }
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255,255,255,0.08);
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.25);
    border-radius: 2px;
    &:hover {
      background: rgba(255,255,255,0.4);
    }
  }
`;

const PostsCol = styled.section`
  background: ${theme.black};
  color: ${theme.white};
  padding: 40px;
  display: flex;
  flex-direction: column;
  border-left: 1px solid rgba(255,255,255,0.08);
  min-height: 75vh;
`;

const BackButton = styled(Link)`
  position: fixed;
  top: 24px;
  left: 24px;
  z-index: 20;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${theme.primary}F2;
  backdrop-filter: blur(8px);
  color: ${theme.black};
  text-decoration: none;
  border-radius: 8px;
  font-family: Nunito, sans-serif;
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  &:hover {
    background: ${theme.primaryLight}FA;
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }
  
  &::before {
    content: '←';
    font-size: 1rem;
    font-weight: 700;
  }
  
  @media (max-width: ${breakpoints.l}px) {
    top: 16px;
    left: 16px;
    padding: 8px 12px;
    font-size: 0.8rem;
  }
`;

interface ColumnProps {
  right?: boolean;
}
const Column = styled.section<ColumnProps>(props => ({
  background: props.right ? theme.surface : theme.black,
  color: theme.white,
  border: 0,
  boxShadow: 'none',
  padding: 0,
  minHeight: 'auto',
}));

// Summary ticker (horizontal)
const TickerBar = styled.div`
  width: 100vw;
  background: linear-gradient(90deg, ${theme.black}, ${theme.surface} 45%, ${theme.black});
  border-bottom: 1px solid rgba(255,255,255,0.08);
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 2;
`;

const TickerTrack = styled.div`
  display: flex;
  align-items: center;
  gap: 48px;
  padding: 6px 0;
  animation: ticker-move 42s linear infinite;
  white-space: nowrap;
  font-family: Nunito, sans-serif;
  font-size: 0.82rem;
  letter-spacing: 0.02em;
  font-weight: 600;
  opacity: 0.9;
  &:hover { animation-play-state: paused; }
  @keyframes ticker-move {
    0% { transform: translateX(0); }
    100% { transform: translateX(-50%); }
  }
`;

const TickerItem = styled.a`
  color: ${theme.white};
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  position: relative;
  padding: 2px 0;
  font-family: Nunito, sans-serif;
  &:hover { text-decoration: underline; }
  &::before {
    content: '\u2022';
    color: ${theme.primary};
    font-size: 0.9em;
    position: relative;
    top: -1px;
  }
`;

const Hero = styled.header`
  background: ${theme.surface};
  padding: 12px 32px;
  padding-left: max(32px, calc((100vw - 1200px) / 2 + 32px));
  padding-right: max(32px, calc((100vw - 1200px) / 2 + 32px));
  width: 100%;
  margin: 0;
  box-sizing: border-box;
`;

const HeroTitle = styled.h1`
  font-size: clamp(28px, 2.5vw + 16px, 40px);
  margin: 2px 0 2px 0;
  color: ${theme.white};
  font-family: Nunito, sans-serif;
`;

const HeroSub = styled.p`
  margin: 0 0 2px 0;
  opacity: 0.8;
  color: ${theme.white};
  font-family: Nunito, sans-serif;
`;

const Controls = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 6px;
`;

const Select = styled.select`
  appearance: none;
  padding: 8px 12px;
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 6px;
  background: ${theme.surface};
  color: ${theme.white};
`;

const CoverageList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 4px;
`;

const CoverageItem = styled.li``;

const CoverageLink = styled.a`
  display: block;
  padding: 14px 12px 14px 64px;
  border: none;
  border-radius: 0;
  text-decoration: none;
  color: ${theme.white};
  background: transparent;
  transition: opacity 160ms ease, transform 120ms ease;
  &:hover {
    opacity: 0.8;
    transform: translateX(2px);
  }
`;

const CoverageTitle = styled.h3`
  margin: 0 0 6px 0;
  font-size: 1.05rem;
  color: ${theme.white};
  font-family: Nunito, sans-serif;
`;

const CoverageMeta = styled.div`
  font-size: 0.85rem;
  opacity: 0.75;
  color: rgba(255,255,255,0.8);
  font-family: Nunito, sans-serif;
`;

interface MediaPageProps {
  data?: {
    allPrismicMedia?: {
      nodes: Array<{
        data: {
          hero_video?: {
            url: string;
          };
        };
      }>;
    };
    prismicFooter?: {
      data: Record<string, unknown>;
    };
  };
}

export default function MediaPage({ data }: MediaPageProps) {
  // Get Prismic video or fallback to static video
  const prismicVideo = data?.allPrismicMedia?.nodes?.[0]?.data?.hero_video?.url;
  const videoSrc = prismicVideo || HeroVideo;

  // Same footer content used by the shared site Layout, so this page's
  // footer matches the rest of the site exactly instead of a bespoke one.
  const footerData = data?.prismicFooter?.data;

  // Filters
  const [year, setYear] = useState<string>("all");
  const [source, setSource] = useState<string>("all");

  // Precompute options and filtered list
  const { years, sources, items } = useMemo(() => {
    const parseYear = (d?: string) => {
      if (!d) return "Unknown";
      const y = new Date(d).getFullYear();
      return isNaN(y) ? "Unknown" : String(y);
    };
    const yearsSet = new Set<string>();
    const sourcesSet = new Set<string>();
    MEDIA_LINKS.forEach(m => {
      yearsSet.add(parseYear(m.date));
      if (m.source) sourcesSet.add(m.source);
    });
    const years = Array.from(yearsSet).filter(Boolean).sort((a,b) => (a === "Unknown" ? 1 : b === "Unknown" ? -1 : Number(b) - Number(a)));
    const sources = Array.from(sourcesSet).sort((a,b) => a.localeCompare(b));

    // Sort by date desc, unknown dates last
    const sorted = [...MEDIA_LINKS].sort((a,b) => {
      const ad = a.date ? new Date(a.date).getTime() : -Infinity;
      const bd = b.date ? new Date(b.date).getTime() : -Infinity;
      return bd - ad;
    });
    return { years, sources, items: sorted };
  }, []);

  const filtered = useMemo(() => {
    return items.filter(m => {
      const y = m.date ? String(new Date(m.date).getFullYear()) : "Unknown";
      const yOk = year === "all" || y === year;
      const sOk = source === "all" || (m.source ?? "") === source;
      return yOk && sOk;
    });
  }, [items, year, source]);
  const summaryTickerItems = useMemo(() => {
    // Take most recent 12 distinct titles (no duplicates)
    const seen = new Set<string>();
    const ordered = items.filter(i => {
      if (seen.has(i.title)) return false;
      seen.add(i.title);
      return true;
    }).slice(0, 12);
    return ordered;
  }, [items]);
  return (
    <Page>
      <FullWidthContainer>
        <video 
          src={videoSrc} 
          autoPlay
          loop
          muted
          playsInline
          onError={(e) => {
            console.log('Video failed to load:', e);
            e.currentTarget.style.display = 'none';
          }}
          style={{ 
            width: '107vw', 
            height: '220px', 
            objectFit: 'cover', 
            objectPosition: '40% 50%', 
            display: 'block', 
            position: 'absolute',
            top: 0,
            left: '-3.5vw',
            margin: 0,
            padding: 0,
            zIndex: 2
          }} 
        />
        <TickerBar aria-label="Recent media highlights">
          <TickerTrack>
            {[...summaryTickerItems, ...summaryTickerItems].map((m, idx) => (
              <TickerItem key={m.url + idx} href={m.url} target="_blank" rel="noopener noreferrer">
                <span>{m.source?.toUpperCase() || 'MEDIA'}: {m.title}</span>
              </TickerItem>
            ))}
          </TickerTrack>
        </TickerBar>
      </FullWidthContainer>
      
            <div style={{ paddingTop: '180px' }}>
        {/* HERO SECTION - COMMENTED OUT - EASILY REVERSIBLE */}
        {/*
        <Hero>
          <HeroTitle>Media</HeroTitle>
          <HeroSub>Recent press coverage and news about Scindo. Filter by year or outlet.</HeroSub>
          <Controls>
            <label>
              <span style={{ marginRight: 8 }}>Year</span>
              <Select value={year} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setYear(e.target.value)}>
                <option value="all">All</option>
                {years.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Select>
            </label>
            <label>
              <span style={{ marginRight: 8 }}>Outlet</span>
              <Select value={source} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSource(e.target.value)}>
                <option value="all">All</option>
                {sources.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Select>
            </label>
          </Controls>
        </Hero>
        */}
        
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Scindo Media",
          "description": "Press coverage and news about Scindo",
          "about": {
            "@type": "Organization",
            "name": "Scindo"
          }
        }) }} />
        
        <Global styles={{
          'html, body': {
            background: theme.black,
            color: theme.white,
            margin: 0,
            padding: 0,
            fontFamily: 'Nunito, sans-serif',
          },
          'h1, h2, h3, h4, h5, h6': {
            fontFamily: 'Nunito, sans-serif',
          },
          '*': {
            boxSizing: 'border-box',
          },
        }} />
        
        <BackButton to="/">Back to Home</BackButton>
        
        <Grid>
          <LeftColumn>
            <h2 style={{ 
              marginBottom: 8, 
              marginRight: 40, 
              color: theme.white, 
              fontFamily: 'Nunito, sans-serif', 
              fontSize: 'clamp(26px, 2.2vw + 16px, 36px)', 
              fontWeight: 600, 
              textAlign: 'left',
              letterSpacing: '-0.02em'
            }}>In the news</h2>
            <NewsPanel>
              {filtered.length === 0 ? (
                <div style={{ padding: '12px 0', opacity: 0.7, color: theme.white }}>
                  No coverage found for the selected filters.
                </div>
              ) : (
                <CoverageList>
                  {filtered.map((m, idx) => (
                    <CoverageItem key={`${m.url}-${idx}`}>
                      <CoverageLink href={m.url} target="_blank" rel="noopener noreferrer">
                        <CoverageTitle>{m.title}</CoverageTitle>
                        <CoverageMeta>
                          {(m.source ?? 'Unknown outlet')}
                          {m.date ? ` — ${new Date(m.date).toLocaleDateString()}` : ''}
                        </CoverageMeta>
                      </CoverageLink>
                    </CoverageItem>
                  ))}
                </CoverageList>
              )}
            </NewsPanel>
          </LeftColumn>
          <PostsCol>
            <h2 style={{
              color: theme.white,
              fontFamily: 'Nunito, sans-serif', 
              fontSize: 'clamp(26px, 2.2vw + 16px, 36px)', 
              fontWeight: 600, 
              textAlign: 'left',
              marginBottom: 24,
              letterSpacing: '-0.02em'
            }}>Posts</h2>
            <BlogList />
          </PostsCol>
        </Grid>
      </div>
      
      {/* ThemeProvider: the shared Footer's styles read props.theme via
          emotion's ThemeProvider context, which this page never otherwise
          sets up (it uses the raw `theme` import directly everywhere else).
          The shared Footer is normally `position: fixed` with a negative
          z-index, revealed only via a scroll-distance spacer tuned to the
          homepage's specific section stack — not worth reproducing on this
          page's much shorter, differently-shaped layout. Forcing it static
          keeps the identical visual design while rendering it as a normal,
          always-visible block at the true end of the page. */}
      <ThemeProvider theme={theme}>
        <StaticFooterWrap>
          <SiteFooter data={footerData} />
        </StaticFooterWrap>
      </ThemeProvider>
    </Page>
  );
}

export const pageQuery = graphql`
  query MediaPageQuery {
    allPrismicMedia(limit: 1) {
      nodes {
        data {
          hero_video {
            url
          }
        }
      }
    }
    prismicFooter {
      data {
        contact_text {
          text
          raw {
            text
          }
        }
        contact_title {
          text
        }
        social {
          social_icon {
            alt
            url
          }
          social_icon_link {
            url
          }
        }
        subscribe_field_text
        subscribe_title {
          text
        }
      }
    }
  }
`;
